/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { ElementType } from 'react';

import { ShowMoreGridSection } from '@coursera-web/cds-labs';
import type { BaseItem, GridConfig } from '@coursera-web/cds-labs';

import type { TrackingData } from 'js/lib/retracked';

import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import useUserAgent from 'bundles/page/hooks/useUserAgent';

type Props<I, T> = {
  title: React.JSX.Element;
  Component: ElementType;
  componentProps: (item: I, itemIndex: number) => T;
  items: I[];
  secondaryCta?: React.JSX.Element;
  itemsGridConfig?: GridConfig;
  numDisplayedRows?: number;
  /**
   * Max number of items to show for every progressive expansion
   * @default 8
   */
  showMoreAmount?: number;
  footer?: React.JSX.Element;
  trackingData?: TrackingData;
  customComponentAtIndex?: { index: number; component: React.JSX.Element };
  disablePadding?: boolean;
};

const styles = {
  disablePadding: css`
    padding: 0;
  `,
};

/**
 * @description A generic collection component that can be used to display a list of items in a grid format.
 */
const GeneralCollection = <I extends BaseItem, T>({
  title,
  Component,
  componentProps,
  items,
  secondaryCta,
  itemsGridConfig,
  numDisplayedRows = 1,
  showMoreAmount,
  footer,
  trackingData,
  customComponentAtIndex,
  disablePadding,
}: Props<I, T>) => {
  const trackV3 = useTracker();
  const userAgent = useUserAgent();

  const placeholderItem = { id: 'PLACEHOLDER_ITEM_ID' } as I;

  const itemsToShow = customComponentAtIndex
    ? [...items.slice(0, customComponentAtIndex.index), placeholderItem, ...items.slice(customComponentAtIndex.index)]
    : items;

  return (
    <ShowMoreGridSection<I>
      css={disablePadding && styles.disablePadding}
      renderTitle={() => title}
      items={itemsToShow}
      itemsGridConfig={itemsGridConfig}
      defaultRows={numDisplayedRows}
      showMoreAmount={showMoreAmount}
      renderItem={({ item, itemIndex, itemRef }) => {
        if (customComponentAtIndex && itemIndex === customComponentAtIndex.index) {
          return customComponentAtIndex.component;
        }
        const itemProps = componentProps(item, itemIndex);
        return <Component {...itemProps} ref={itemRef} />;
      }}
      renderExpandButton={({ onClick, defaultLabel, ...rest }) => {
        return (
          <Button
            size="small"
            key="showMore"
            variant="secondary"
            onClick={() => {
              trackV3('expand_carousel', { carousel: trackingData?.carousel });
              onClick();
            }}
            {...rest}
          >
            {defaultLabel}
          </Button>
        );
      }}
      renderSecondaryCta={() => {
        return secondaryCta;
      }}
      renderFooter={() => footer}
      spacing={{ xs: 16, sm: 24 }}
      defaultMobileBreakpointMatches={userAgent && userAgent.isMobileBrowser}
    />
  );
};

export default GeneralCollection;
